import { currencyFormat } from './currency'
import { digitsFormat } from './digits'
import { floatFormat } from './float'
import { integerFormat } from './integer'
import { phoneFormat } from './phone'
import { InputMask } from './types'

/**
 * Controls which characters are allowed while typing and how the value is
 * normalized on blur. The `value` is ALWAYS handled as a string; `format` only
 * affects input filtering and an optional blur-time normalization.
 *
 * Choosing between `digits` and `integer` is the common point of confusion, so
 * read those two entries carefully.
 */
export type InputFormat =
  /** Default. No filtering and no normalization — any text is accepted as-is. */
  | 'string'
  /**
   * A numeric string with up to two decimal places (e.g. a money amount).
   * Filters input to `/^\d*([.]?\d{0,2})?$/` and, on blur, normalizes via
   * `Number(value).toFixed(2)` (e.g. `5` -> `5.00`). Prefixes a `$` and
   * defaults the placeholder to `0.00`.
   */
  | 'currency'
  /**
   * A floating-point numeric string. Filters input to values where
   * `Number(value)` is finite and, on blur, normalizes via `String(Number(value))`
   * (e.g. `1.50` -> `1.5`). Defaults the placeholder to `0.0`.
   */
  | 'float'
  /**
   * An integer numeric *quantity*. Filters input to digits only (`/^\d*$/`) and,
   * on blur, normalizes via `Number(value).toFixed(0)`.
   *
   * IMPORTANT: this is an older format and its blur normalization is LOSSY by
   * design — it runs the value through `Number(...)`, which:
   *   - strips leading zeros (`007` -> `7`), and
   *   - loses precision for values beyond `Number.MAX_SAFE_INTEGER`
   *     (~16 digits), corrupting long digit strings.
   * This behavior is intentional for integer quantities and CANNOT be changed
   * without breaking the many existing callers that depend on it. If you need to
   * preserve the literal characters the user typed (leading zeros, long
   * fixed-length codes), use `digits` instead — NOT `integer`.
   */
  | 'integer'
  /** A phone number. Reformats input via `formatPhoneInput` as the user types. */
  | 'phone'
  /**
   * A digits-only *string* (e.g. card number, CVC, ZIP). Filters input to digits
   * only (`/^\d*$/`), exactly like `integer`, but performs NO blur-time
   * normalization, so the value is kept verbatim.
   *
   * Use this instead of `integer` whenever the digits are an identifier rather
   * than a quantity: it preserves leading zeros (`01234` stays `01234`) and
   * arbitrarily long values (16-19 digit card numbers) that `integer` would
   * corrupt. There is no default placeholder.
   */
  | 'digits'

const formats: { [F in Exclude<InputFormat, 'string'>]: InputMask } = {
  currency: currencyFormat,
  float: floatFormat,
  integer: integerFormat,
  phone: phoneFormat,
  digits: digitsFormat,
}

/**
 * Resolves an {@link InputFormat} name to its built-in mask. `string` means
 * "no masking at all", so it deliberately resolves to `undefined` rather than
 * to a pass-through mask.
 */
export const resolveFormat = (format: InputFormat): InputMask | undefined =>
  format === 'string' ? undefined : formats[format]
